"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.FlowDomain = void 0;
const logger_1 = require("@upflowy/logger");
const types_1 = require("@upflowy/types");
const xstate_helpers_1 = require("@upflowy/xstate-helpers");
const xstate_path_mapper_1 = require("@upflowy/xstate-helpers/lib/xstate-path-mapper");
const firestore_1 = require("firebase/firestore");
const lodash_1 = require("lodash");
const short_uuid_1 = __importDefault(require("short-uuid"));
const domain_utils_1 = require("./domain-utils");
const migrate_options_1 = __importDefault(require("./migrate-options"));
const organisation_domain_1 = require("./organisation-domain");
const EntityNotFoundError_1 = require("../errors/EntityNotFoundError");
const FlowNotFoundError_1 = require("../errors/FlowNotFoundError");
const FlowVersionNotFoundError_1 = __importDefault(require("../errors/FlowVersionNotFoundError"));
const helpers_1 = require("../helpers");
const firebase_1 = require("../services/firebase");
const use_upflowy_store_1 = require("../use-upflowy-store");
class FlowDomain {
    constructor(db, ref) {
        this.db = db;
        this.ref = ref;
        return this;
    }
    static findById(db, flowId) {
        const flowRef = (0, use_upflowy_store_1.upDoc)((0, use_upflowy_store_1.upCollection)(db, 'Flows'), flowId);
        return new FlowDomain(db, flowRef);
    }
    async getDoc() {
        return (await (0, firestore_1.getDoc)(this.ref)).data();
    }
    async getVersionsOfStatus(status, lmt) {
        const qry = (0, firestore_1.query)((0, use_upflowy_store_1.upCollection)(this.ref, 'versions'), (0, firestore_1.where)('status', '==', status), ...(lmt ? [(0, firestore_1.limit)(lmt)] : []));
        const versions = (await (0, firestore_1.getDocs)(qry)).docs;
        if (!versions.length)
            return null;
        return versions;
    }
    static visibleStepsIds(flowVersion) {
        if (flowVersion.flowPath) {
            return (0, xstate_path_mapper_1.getStepIds)(xstate_helpers_1.flowPathToXstate.default(JSON.parse(flowVersion.flowPath)));
        }
        if (flowVersion.machine) {
            return (0, xstate_path_mapper_1.getStepIds)(flowVersion.machine);
        }
        return [];
    }
    static async visibleWebhooksIds(flowVersionRef, flowVersion) {
        const chunckedStepIds = (0, helpers_1.chunkArray)(this.visibleStepsIds(flowVersion), 10);
        // We need to chunk the array because the 'in' WhereFilterOp is limited to an array of
        // at most 10 elements
        const webhooksIdsQueriesPromises = chunckedStepIds.map((stepIds) => (0, firestore_1.getDocs)((0, firestore_1.query)((0, firestore_1.collection)(flowVersionRef, 'webhooks'), (0, firestore_1.where)('stepId', 'in', stepIds))));
        // Execute query to get all documents
        const webhooksDocs = await Promise.all(webhooksIdsQueriesPromises);
        // For each query, extract document id of all documents
        const webhooksIds = [];
        webhooksDocs.forEach((snapshots) => snapshots.forEach((snapshot) => {
            var _a;
            webhooksIds.push((_a = snapshot.data().id) !== null && _a !== void 0 ? _a : '');
        }));
        // Flatten and delete duplicated ids
        return [...new Set(webhooksIds.flat(1))];
    }
    async getVersionsOfAppVersion(appVersion, lmt) {
        const qry = (0, firestore_1.query)((0, use_upflowy_store_1.upCollection)(this.ref, 'versions'), (0, firestore_1.where)('appVersion', '==', appVersion), ...(lmt ? [(0, firestore_1.limit)(lmt)] : []));
        const versions = (await (0, firestore_1.getDocs)(qry)).docs;
        if (!versions.length)
            return null;
        return versions;
    }
    async getVersionsRef() {
        const qry = (0, use_upflowy_store_1.upCollection)(this.ref, 'versions');
        return (await (0, firestore_1.getDocs)(qry)).docs;
    }
    async getVersionById(id) {
        const qry = (0, firestore_1.query)((0, use_upflowy_store_1.upCollection)(this.ref, 'versions'), (0, firestore_1.where)('id', '==', id), (0, firestore_1.limit)(1));
        const versions = (await (0, firestore_1.getDocs)(qry)).docs;
        if (!versions.length)
            return null;
        return versions[0];
    }
    async getDraftVersionRef() {
        const draft = await this.getVersionsOfStatus(types_1.FlowVersionStatus.DRAFT, 1);
        if (!draft)
            return null;
        return draft[0].ref;
    }
    async getLiveVersionRef() {
        const live = await this.getVersionsOfStatus(types_1.FlowVersionStatus.PUBLISHED, 1);
        if (!live)
            return null;
        return live[0].ref;
    }
    async getOrganisationDomain() {
        const flow = await this.getDoc();
        return flow ? organisation_domain_1.OrganisationDomain.findById(this.db, flow.organisationId) : undefined;
    }
    async getRemoteConfigTriggers(versionRef) {
        const triggers = (await (0, firestore_1.getDocs)((0, firestore_1.collection)(versionRef, 'triggers'))).docs;
        const flowId = this.ref.id;
        const remoteConfigTriggers = triggers.map((trigger) => {
            const { id, origin, action, params, name } = trigger.data();
            return {
                triggerName: name,
                triggerId: id,
                origin,
                action,
                params,
                flowId,
            };
        });
        return remoteConfigTriggers;
    }
    async createDraftFromVersion(versionId, overwrite = false, appVersion) {
        let draftVersionRef = await this.getDraftVersionRef();
        if (!overwrite && draftVersionRef) {
            throw new Error('Draft Version Already Exists. Did you mean to overwrite it?');
        }
        if (!draftVersionRef) {
            const draftFlowVersionId = (0, short_uuid_1.default)().generate();
            draftVersionRef = (0, use_upflowy_store_1.upDoc)((0, use_upflowy_store_1.upCollection)(this.ref, 'versions'), draftFlowVersionId);
        }
        const batch = (0, firestore_1.writeBatch)(this.db);
        const newDraftVersionRef = await this.createDraftVersionInternal(batch, versionId, draftVersionRef.id, appVersion);
        await batch.commit();
        return newDraftVersionRef;
    }
    async createDraftVersionInternal(batch, sourceVersionId, draftVersionId, appVersion = '1.0.0') {
        var _a, _b, _c, _d, _e, _f, _g, _h, _j;
        const sourceVersionRef = (0, use_upflowy_store_1.upDoc)((0, use_upflowy_store_1.upCollection)(this.ref, 'versions'), sourceVersionId);
        const sourceVersionDoc = await (0, firestore_1.getDoc)(sourceVersionRef);
        const sourceVersionData = sourceVersionDoc.data();
        if (!sourceVersionData) {
            throw new FlowVersionNotFoundError_1.default(`Version Not Found, looking by id ${sourceVersionId}`);
        }
        const defaultThemes = await (0, firestore_1.getDocs)((0, firestore_1.query)((0, use_upflowy_store_1.upCollection)(this.db, 'Themes'), (0, firestore_1.where)('defaultTheme', '==', true), (0, firestore_1.where)('active', '==', true), (0, firestore_1.limit)(1)));
        const defaultTheme = (_a = defaultThemes.docs[0]) === null || _a === void 0 ? void 0 : _a.data();
        const activeThemeVersions = await (0, firestore_1.getDocs)((0, firestore_1.query)((0, use_upflowy_store_1.upCollection)((0, use_upflowy_store_1.upDoc)((0, use_upflowy_store_1.upCollection)(this.db, 'Themes'), defaultTheme.id), 'versions'), (0, firestore_1.where)('active', '==', true), (0, firestore_1.limit)(1)));
        const activeThemeVersion = (_b = activeThemeVersions.docs[0]) === null || _b === void 0 ? void 0 : _b.data();
        const activeThemeVersionRef = activeThemeVersion
            ? `Themes/${defaultTheme.id}/versions/${activeThemeVersion === null || activeThemeVersion === void 0 ? void 0 : activeThemeVersion.id}`
            : '';
        !activeThemeVersion &&
            logger_1.logger.log({
                message: 'Active Theme version not found',
                context: defaultTheme,
            });
        const draftVersionRef = (0, use_upflowy_store_1.upDoc)((0, use_upflowy_store_1.upCollection)(this.ref, 'versions'), draftVersionId);
        const name = `V${parseInt(sourceVersionData.name.substring(1), 10) + 1}`;
        const createdAt = new Date().getTime();
        await (0, domain_utils_1.deleteCollection)({
            ref: draftVersionRef,
            collectionPath: 'triggers',
        }, batch);
        await (0, domain_utils_1.deleteCollection)({
            ref: draftVersionRef,
            collectionPath: 'steps',
        }, batch);
        await (0, domain_utils_1.deleteCollection)({
            ref: draftVersionRef,
            collectionPath: 'webhooks',
            subcollectionPaths: ['private'],
        }, batch);
        await (0, domain_utils_1.deleteCollection)({
            ref: draftVersionRef,
            collectionPath: 'assets',
        }, batch);
        await (0, domain_utils_1.deleteCollection)({
            ref: draftVersionRef,
            collectionPath: 'statusLog',
        }, batch);
        let newOptions = (_d = (_c = sourceVersionData === null || sourceVersionData === void 0 ? void 0 : sourceVersionData.meta) === null || _c === void 0 ? void 0 : _c.options) !== null && _d !== void 0 ? _d : {};
        // Removing this as themeVersionRef does not exist in ThemeDoc
        const defaultThemeRef = (_e = defaultThemes.docs[0]) === null || _e === void 0 ? void 0 : _e.ref.path;
        if (((_f = sourceVersionData === null || sourceVersionData === void 0 ? void 0 : sourceVersionData.meta) === null || _f === void 0 ? void 0 : _f.themeVersionRef) !== defaultThemeRef) {
            newOptions = (0, migrate_options_1.default)((_h = (_g = sourceVersionData === null || sourceVersionData === void 0 ? void 0 : sourceVersionData.meta) === null || _g === void 0 ? void 0 : _g.options) !== null && _h !== void 0 ? _h : {}, defaultTheme.metaOptions);
        }
        const draftVersionData = {
            ...sourceVersionData,
            meta: {
                ...sourceVersionData.meta,
                themeId: (_j = defaultTheme === null || defaultTheme === void 0 ? void 0 : defaultTheme.id) !== null && _j !== void 0 ? _j : '',
                themeVersionRef: activeThemeVersionRef,
                options: newOptions,
                pluginVersion: 'v1',
                showWaterMark: true,
            },
            id: draftVersionId,
            copiedFrom: sourceVersionId,
            startDate: null,
            endDate: null,
            name,
            createdAt,
            updatedAt: createdAt,
            status: types_1.FlowVersionStatus.DRAFT,
            appVersion,
        };
        batch.set(draftVersionRef, draftVersionData, { merge: true });
        const stepIds = FlowDomain.visibleStepsIds(sourceVersionData);
        const webhooksIds = await FlowDomain.visibleWebhooksIds(sourceVersionRef, sourceVersionData);
        await (0, domain_utils_1.copyAllFlowVersionCollections)({
            sourceRef: sourceVersionRef,
            targetRef: draftVersionRef,
            filterStepsFunction: (document) => stepIds.includes(document.id),
            filterWebhooksFunction: (document) => webhooksIds.includes(document.id),
        }, batch);
        return draftVersionRef;
    }
    async copyVersionToNewFlow(versionToCopy, options = {
        userId: undefined,
        title: undefined,
    }) {
        const { userId, title } = options;
        const timestamp = (0, helpers_1.currentTimeStamp)();
        const currentFlow = await this.getDoc();
        if (!currentFlow)
            throw new FlowNotFoundError_1.FlowNotFoundError();
        const flowId = (0, short_uuid_1.default)().generate();
        const flow = await (0, use_upflowy_store_1.upDoc)((0, use_upflowy_store_1.upCollection)(this.db, 'Flows'), flowId);
        const batch = (0, firestore_1.writeBatch)(this.db);
        batch.set(flow, {
            id: flowId,
            nanoId: null,
            slug: null,
            customURL: null,
            organisationId: currentFlow.organisationId,
            createdAt: timestamp,
            updatedAt: timestamp,
            deletedAt: null,
            copiedFrom: currentFlow.id,
            ownerRef: userId !== null && userId !== void 0 ? userId : currentFlow.ownerRef,
            title: title !== null && title !== void 0 ? title : `${currentFlow.title} (copy)`,
        });
        if (!versionToCopy)
            throw new FlowVersionNotFoundError_1.default('Version Not Found, looking by id');
        return FlowDomain.copyFlowVersionFromOtherFlowInternal(this.db, batch, flowId, currentFlow.id, versionToCopy, 'v1');
    }
    async copyFlowVersionFromOtherFlow(copiedFromFlowId, copiedFromVersionId, name) {
        const flowId = this.getId();
        return FlowDomain.copyFlowVersionFromOtherFlowInternal(this.db, (0, firestore_1.writeBatch)(this.db), flowId, copiedFromFlowId, copiedFromVersionId, name);
    }
    static async copyFlowVersionFromOtherFlowInternal(db, batch, flowId, copiedFromFlowId, copiedFromVersionId, name) {
        const timeStamp = (0, helpers_1.currentTimeStamp)();
        const flowVersionId = (0, short_uuid_1.default)().generate();
        const newVersionRef = (0, use_upflowy_store_1.upDoc)((0, use_upflowy_store_1.upCollection)((0, use_upflowy_store_1.upDoc)((0, use_upflowy_store_1.upCollection)(db, 'Flows'), flowId), 'versions'), flowVersionId);
        const copiedVersionRef = (0, use_upflowy_store_1.upDoc)((0, use_upflowy_store_1.upCollection)((0, use_upflowy_store_1.upDoc)((0, use_upflowy_store_1.upCollection)(db, 'Flows'), copiedFromFlowId), 'versions'), copiedFromVersionId);
        const copiedVersion = (await (0, firestore_1.getDoc)(copiedVersionRef)).data();
        if (!copiedVersion)
            throw new FlowVersionNotFoundError_1.default('Version Not Found');
        batch.set(newVersionRef, {
            ...copiedVersion,
            startDate: null,
            createdAt: timeStamp,
            updatedAt: timeStamp,
            status: types_1.FlowVersionStatus.DRAFT,
            name: name !== null && name !== void 0 ? name : 'default name',
            id: flowVersionId,
            copiedFrom: copiedFromVersionId,
        }, { merge: true });
        await (0, domain_utils_1.copyAllFlowVersionCollections)({
            sourceRef: copiedVersionRef,
            targetRef: newVersionRef,
        }, batch);
        await batch.commit();
        return {
            flowDomain: FlowDomain.findById(db, flowId),
            flowId,
            flowVersionId,
        };
    }
    async transfer(organisationId) {
        const liveVersionRef = await this.getLiveVersionRef();
        if (liveVersionRef) {
            const beforeOrgDomain = await this.getOrganisationDomain();
            if (!beforeOrgDomain)
                throw new EntityNotFoundError_1.EntityNotFoundError('organisation');
            const { config } = await beforeOrgDomain.getRemoteConfigInfo();
            const remoteConfigTriggers = config.triggers.filter((trigger) => trigger.flowId === this.ref.id);
            await beforeOrgDomain.removeFlowTriggersFromRemoteConfig(this.ref.id);
            const afterOrgDomain = organisation_domain_1.OrganisationDomain.findById(this.db, organisationId);
            await afterOrgDomain.setFlowTriggersInRemoteConfig({
                flowId: this.ref.id,
                remoteConfigTriggers,
            });
        }
        await (0, firestore_1.setDoc)(this.ref, { organisationId }, { merge: true });
        return this;
    }
    async rename(title) {
        const now = (0, helpers_1.currentTimeStamp)();
        await (0, firestore_1.setDoc)(this.ref, { title, updatedAt: now }, { merge: true });
        return this;
    }
    async getMostRecentVersionDoc() {
        const qry = (0, firestore_1.query)((0, use_upflowy_store_1.upCollection)(this.ref, 'versions'), (0, firestore_1.orderBy)('startDate', 'desc'), (0, firestore_1.limit)(1));
        const versions = (await (0, firestore_1.getDocs)(qry)).docs;
        if (!versions.length || versions.length === 0)
            return null;
        return versions[0].data();
    }
    async deleteDraft(versionId) {
        const versionRef = (0, use_upflowy_store_1.upDoc)((0, use_upflowy_store_1.upCollection)(this.ref, 'versions'), versionId);
        const doc = (await (0, firestore_1.getDoc)(versionRef)).data();
        if ((doc === null || doc === void 0 ? void 0 : doc.status) !== 'DRAFT')
            throw new Error(`Flow version ${versionId} is not a draft`);
        const batch = (0, firestore_1.writeBatch)(firebase_1.db);
        batch.delete(versionRef);
        await Promise.all([
            (0, domain_utils_1.deleteCollection)({
                ref: versionRef,
                collectionPath: 'steps',
            }, batch),
            (0, domain_utils_1.deleteCollection)({
                ref: versionRef,
                collectionPath: 'assets',
            }, batch),
            (0, domain_utils_1.deleteCollection)({
                ref: versionRef,
                collectionPath: 'statusLog',
            }, batch),
        ]);
        await batch.commit();
    }
    getId() {
        return this.ref.id;
    }
    async getPublishedAndInactiveFields() {
        const qry = (0, firestore_1.query)((0, use_upflowy_store_1.upCollection)(this.ref, 'versions'), (0, firestore_1.where)('status', '!=', 'DRAFT'));
        const publishedAndInactiveVersions = (await (0, firestore_1.getDocs)(qry)).docs;
        // Fetching all the custom column names
        const tmpColumns = [];
        await Promise.all(publishedAndInactiveVersions.map(async (flowVersion) => {
            const steps = (await (0, firestore_1.getDocs)((0, firestore_1.collection)(flowVersion.ref, 'steps'))).docs.map((step) => step.data());
            const flowVersionFields = (0, helpers_1.parseStepDocumentFields)(steps);
            tmpColumns.push(...flowVersionFields);
        }));
        return (0, lodash_1.uniqBy)(tmpColumns, (e) => e.namespacedName);
    }
}
exports.FlowDomain = FlowDomain;
//# sourceMappingURL=flow-domain.js.map